/** Where the per-tab session id lives. */
const SESSION_STORAGE_KEY = 'dt.analytics.sessionId';

/**
 * Get (or mint) the id grouping this tab's events into one sitting.
 *
 * Session storage rather than local storage on purpose: two tabs are two
 * parallel pieces of work, and merging them into one id would interleave their
 * events and make the resulting "workflow" a fiction. It is a grouping key
 * only — the server never authorizes anything on it, and treats it as
 * untrusted client input.
 *
 * @param storage - Injected so tests need no real browser storage.
 * @param createId - Injected so tests get a deterministic id.
 * @returns The session id, or an ephemeral one if storage is unavailable
 *          (private-mode browsers throw on access).
 */
export function getSessionId(
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined,
  createId: () => string = () => crypto.randomUUID(),
): string {
  try {
    const existing = storage?.getItem(SESSION_STORAGE_KEY);
    if (existing !== null && existing !== undefined && existing !== '') {
      return existing;
    }

    const created = createId();
    storage?.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return createId();
  }
}
